import type { RunMapStop, ClickedRunStop } from '../runTypes';
import { RUN_STOP_COLORS } from '../runTypes';
import { FLAG_PATH } from '../constants';

// SVG path for a house/home marker (24x24 viewBox)
const HOUSE_PATH = 'M12 3L2 12h3v8h6v-6h2v6h6v-8h3L12 3z';
const DEALERSHIP_COLOR = '#3b82f6'; // blue-500

// TYPES --------------------------------------------------------------------- //

interface CreateRunStopOverlaysOptions {
  map: google.maps.Map;
  stops: RunMapStop[];
  selectedStopIndex?: number | null;
  onClickStop?: (stop: ClickedRunStop | null) => void;
  clickListeners?: google.maps.MapsEventListener[];
}

interface RunStopOverlayResult {
  markers: google.maps.Marker[];
  geofences: (google.maps.Circle | google.maps.Polygon)[];
}

// HELPERS ------------------------------------------------------------------- //

function createStopGeofence(
  map: google.maps.Map,
  stop: RunMapStop,
  color: string,
  renderedIds: Set<number>,
  geofences: (google.maps.Circle | google.maps.Polygon)[],
  isSelected?: boolean,
  isDimmed?: boolean,
): void {
  const loc = stop.location;
  if (!loc) return;
  if (renderedIds.has(loc.id)) return;
  renderedIds.add(loc.id);

  const center = {
    lat: loc.geofence_center_lat ?? loc.latitude ?? stop.lat,
    lng: loc.geofence_center_lng ?? loc.longitude ?? stop.lng,
  };

  const fillOpacity = isSelected ? 0.35 : isDimmed ? 0.08 : 0.2;
  const strokeOpacity = isSelected ? 1 : isDimmed ? 0.3 : 0.8;
  const strokeWeight = isSelected ? 3 : 2;

  if (loc.geofence_vertices && Array.isArray(loc.geofence_vertices) && loc.geofence_vertices.length >= 3) {
    geofences.push(
      new google.maps.Polygon({
        map,
        paths: loc.geofence_vertices,
        fillColor: color,
        fillOpacity,
        strokeColor: color,
        strokeOpacity,
        strokeWeight,
        clickable: false,
        zIndex: isSelected ? 7 : 2,
      }),
    );
  } else {
    geofences.push(
      new google.maps.Circle({
        map,
        center,
        radius: loc.geofence_radius ?? 200,
        fillColor: color,
        fillOpacity,
        strokeColor: color,
        strokeOpacity,
        strokeWeight,
        clickable: false,
        zIndex: isSelected ? 7 : 2,
      }),
    );
  }
}

// FACTORY ------------------------------------------------------------------- //

/**
 * Creates numbered flag markers + geofences for each stop in a mobility run.
 * Markers are colored by stop phase (pending/active/completed/skipped).
 */
export function createRunStopOverlays({
  map,
  stops,
  selectedStopIndex,
  onClickStop,
  clickListeners,
}: CreateRunStopOverlaysOptions): RunStopOverlayResult {
  const markers: google.maps.Marker[] = [];
  const geofences: (google.maps.Circle | google.maps.Polygon)[] = [];
  const renderedLocationIds = new Set<number>();

  stops.forEach((stop, idx) => {
    if (stop.lat == null || stop.lng == null) return;

    const isSelected = selectedStopIndex === idx;
    const isDimmed = selectedStopIndex != null && !isSelected;
    const color = RUN_STOP_COLORS[stop.phase];

    // Geofence
    createStopGeofence(map, stop, color, renderedLocationIds, geofences, isSelected, isDimmed);

    // First and last stops are the dealership — only render one marker (skip last)
    const isDealershipStop = idx === 0 || idx === stops.length - 1;
    if (idx === stops.length - 1 && stops.length > 1) return; // skip duplicate dealership marker

    // Marker: house icon for dealership stops, flag for service stops
    const marker = new google.maps.Marker({
      map,
      position: { lat: stop.lat, lng: stop.lng },
      icon: isDealershipStop ? {
        path: HOUSE_PATH,
        fillColor: DEALERSHIP_COLOR,
        fillOpacity: isDimmed ? 0.4 : 1,
        strokeColor: '#ffffff',
        strokeWeight: isSelected ? 2.5 : 1.5,
        scale: isSelected ? 1.8 : 1.4,
        anchor: new google.maps.Point(12, 22),
      } : {
        path: FLAG_PATH,
        fillColor: color,
        fillOpacity: isDimmed ? 0.4 : 1,
        strokeColor: '#ffffff',
        strokeWeight: isSelected ? 2.5 : 1.5,
        scale: isSelected ? 1.6 : 1.2,
        anchor: new google.maps.Point(6, 21),
      },
      label: isDealershipStop ? undefined : {
        text: String(stop.stopOrder),
        color: '#ffffff',
        fontWeight: '700',
        fontSize: '11px',
      },
      clickable: true,
      cursor: 'pointer',
      zIndex: 20 + stop.stopOrder,
    });

    if (onClickStop) {
      const listener = marker.addListener('click', (e: google.maps.MapMouseEvent) => {
        const pixel = e.domEvent as MouseEvent;
        onClickStop({ stop, screenX: pixel.clientX, screenY: pixel.clientY });
      });
      clickListeners?.push(listener);
    }

    markers.push(marker);
  });

  return { markers, geofences };
}
